import * as Sentry from '@sentry/react';
import { Route, Routes } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';

import { Signup } from 'components/Signup/Signup';
import { Landing } from 'components/Landing';
import { ProtectedRoute } from 'components/ProtectedRoute';
import WaitingList from 'components/ContingencyWaitingList/WaitingList';
import ThirdPartyScripts from 'Marketing/ThirdPartyScripts';
import { LoginRoutes } from 'components/Login/Routes';
import { usePageTracking } from 'utils/adobeAnalytics/hooks/usePageTracking';
import { BackgroundVariant } from 'components/Landing/component/Background';
import { ThemeInjector } from '@veeqo/ui';
import { DynamicHead } from 'components/DynamicHead';
import { VQMetrics } from './components/Landing/utils/vqMetrics';

// Initialize metrics instance for global use
// eslint-disable-next-line no-underscore-dangle

window.vqMetrics = new VQMetrics(false);

const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

type RouteType = {
  prefix: string;
  path: string;
  contentType: 'veeqo' | 'seCe';
  vqHost: 'SeCe' | 'standalone';
  entryType: 'landing' | 'lwsc' | 'insc';
};

const seCeRouteList: RouteType[] = [
  {
    prefix: '/vq/sc/',
    path: 'landing',
    vqHost: 'SeCe',
    contentType: 'veeqo',
    entryType: 'landing',
  },
  {
    prefix: '/vq/sc/',
    path: 'sc-landing',
    vqHost: 'SeCe',
    contentType: 'seCe',
    entryType: 'landing',
  },
  {
    prefix: '/vq/sc/lwsc/',
    path: 'setup',
    vqHost: 'SeCe',
    contentType: 'veeqo',
    entryType: 'lwsc',
  },
  {
    prefix: '/',
    path: 'landing',
    vqHost: 'standalone',
    contentType: 'veeqo',
    entryType: 'landing',
  },
  {
    prefix: '/',
    path: 'sc-landing',
    vqHost: 'standalone',
    contentType: 'seCe',
    entryType: 'landing',
  },
  { prefix: '/', path: 'setup', vqHost: 'standalone', contentType: 'veeqo', entryType: 'landing' },
  {
    prefix: '/vq/sc/insc/',
    path: 'setup',
    vqHost: 'SeCe',
    contentType: 'veeqo',
    entryType: 'insc',
  },
];

function App() {
  usePageTracking();

  const seCeRoutes = seCeRouteList.map(({ prefix, path, vqHost, contentType, entryType }) => (
    <Route
      path={`${prefix}${path}`}
      key={`${prefix}${path}`}
      element={
        <ProtectedRoute vqHost={vqHost}>
          <Landing
            backgroundVariant={BackgroundVariant.Glow}
            colorVariant="blue"
            cardVariant="default"
            contentType={contentType}
            entryType={entryType}
            footer
          />
        </ProtectedRoute>
      }
    />
  ));

  return (
    <>
      <DynamicHead />
      <ThirdPartyScripts />
      <ThemeInjector />
      <AnimatePresence mode="wait" initial={false}>
        <SentryRoutes key="Signup App">
          <Route path="/signin/*" Component={LoginRoutes} />
          <Route path="/signup/waiting-list" element={<WaitingList />} />
          <Route path="/static_content/waiting-list.html" element={<WaitingList />} />
          <Route path="/signup/*" element={<Signup />} />
          <Route path="/vq/sc/signin/*" element={<LoginRoutes />} />
          <Route path="/vq/sc/signup/*" element={<Signup />} />
          {seCeRoutes}
        </SentryRoutes>
      </AnimatePresence>
    </>
  );
}

export default App;
